import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import "./MapTab.scss";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import { useSelector } from "react-redux";


const W = 800;
const H = 500;
const CX = W / 2;
const CY = H / 2;
const MIN_NODE_R = 18;
const MAX_NODE_R = 44;
const OWNER_R = 50;
const BLOOM_R = 165; // distance from owner to peripheral nodes

const COLORS = {
  owner: {
    fill: "#534AB7",
    ring: "#534AB7",
    badge: "#EEEDFE",
    badgeText: "#3C3489",
  },
  selfDuplicate: {
    fill: "#7F77DD",
    ring: "#7F77DD",
    badge: "#EEEDFE",
    badgeText: "#3C3489",
  },
  duplicate: {
    fill: "#D85A30",
    ring: "#D85A30",
    badge: "#FAECE7",
    badgeText: "#712B13",
  },
};


function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function classifyEntry(entry, ownerUsername) {
  return entry.duplicateActor.username === ownerUsername
    ? "selfDuplicate"
    : "duplicate";
}

function toUserRow(entry, ownerActorId) {
  return {
    id: `${entry.duplicateActor.id}-${entry.directory ?? "map"}-0`,
    username: entry.duplicateActor.username,
    email: entry.duplicateActor.email,
    directory: entry.directory ?? null,
    fileCount: entry.fileCount,
    duplicateActorId: entry.duplicateActor.id,
    actorId: ownerActorId,
  };
}


function computeLayout(content, ownerUsername) {
  const maxCount = Math.max(...content.map((d) => d.fileCount), 1);

  function nodeR(fc) {
    return MIN_NODE_R + (fc / maxCount) * (MAX_NODE_R - MIN_NODE_R);
  }

  const periph = content.map((entry, i) => {
    const role = classifyEntry(entry, ownerUsername);
    const r =
      role === "selfDuplicate"
        ? nodeR(entry.fileCount) + 4
        : nodeR(entry.fileCount);
    const angle = ((2 * Math.PI) / content.length) * i - Math.PI / 2;
    return {
      id: entry.duplicateActor.id,
      username: entry.duplicateActor.username,
      email: entry.duplicateActor.email,
      fileCount: entry.fileCount,
      role,
      r,
      // base offsets relative to center — used when owner is dragged
      ox: Math.cos(angle) * BLOOM_R,
      oy: Math.sin(angle) * BLOOM_R,
      x: CX + Math.cos(angle) * BLOOM_R,
      y: CY + Math.sin(angle) * BLOOM_R,
      fx: CX + Math.cos(angle) * BLOOM_R,
      fy: CY + Math.sin(angle) * BLOOM_R,
      entry,
    };
  });

  const ownerFileCount = content.reduce((s, d) => s + d.fileCount, 0);
  const ownerNode = {
    id: "owner",
    username: ownerUsername,
    email: "",
    fileCount: ownerFileCount,
    role: "owner",
    r: OWNER_R,
    ox: 0,
    oy: 0,
    x: CX,
    y: CY,
    fx: CX,
    fy: CY,
    entry: {
      duplicateActor: { id: "owner", username: ownerUsername, email: "" },
      fileCount: ownerFileCount,
    },
  };

  return { ownerNode, periph };
}


function buildGraph(container, content, drive, onUserClick) {
  d3.select(container).selectAll("*").remove();
  if (!content || content.length === 0) return;

  const ownerUsername = drive?.driveName ?? "";
  const ownerActorId = drive?.enterpriseActorId ?? null;

  const { ownerNode, periph } = computeLayout(content, ownerUsername);
  const allNodes = [ownerNode, ...periph];

  const links = periph.map((n) => ({
    source: ownerNode,
    target: n,
    role: n.role,
  }));

  const svg = d3
    .select(container)
    .append("svg")
    .attr("width", "100%")
    .attr("height", "100%")
    .attr("viewBox", `0 0 ${W} ${H}`)
    .style("overflow", "hidden")
    .attr("aria-label", "Duplicate user relationship graph");

  const zoomBehaviour = d3
    .zoom()
    .scaleExtent([0.25, 4])
    .filter((event) => {
      // Allow wheel zoom and pan via middle-mouse / background drag only
      // so node drag doesn't conflict with pan
      if (event.type === "mousedown")
        return event.target === svg.node() || event.target.tagName === "svg";
      return true;
    })
    .on("zoom", (event) => root.attr("transform", event.transform));

  svg.call(zoomBehaviour);
  

  container.__zoomIn = () =>
    svg.transition().duration(280).call(zoomBehaviour.scaleBy, 1.4);
  container.__zoomOut = () =>
    svg.transition().duration(280).call(zoomBehaviour.scaleBy, 0.72);
  container.__reset = () =>
    svg
      .transition()
      .duration(380)
      .call(zoomBehaviour.transform, d3.zoomIdentity);

  const defs = svg.append("defs");
  svg.call(zoomBehaviour).on("dblclick.zoom", null);

      svg.on("dblclick", () => {
        container.__reset?.();
      });

  // Arrow on edge (radiates OUTWARD from owner → points at duplicate node)
  defs
    .append("marker")
    .attr("id", "arr-dup")
    .attr("viewBox", "0 0 10 10")
    .attr("refX", 9)
    .attr("refY", 5)
    .attr("markerWidth", 6)
    .attr("markerHeight", 6)
    .attr("orient", "auto")
    .append("path")
    .attr("d", "M1 1L9 5L1 9")
    .attr("fill", "none")
    .attr("stroke", "#D85A30")
    .attr("stroke-width", "1.5")
    .attr("stroke-linecap", "round")
    .attr("stroke-linejoin", "round");

  defs
    .append("marker")
    .attr("id", "arr-self")
    .attr("viewBox", "0 0 10 10")
    .attr("refX", 9)
    .attr("refY", 5)
    .attr("markerWidth", 6)
    .attr("markerHeight", 6)
    .attr("orient", "auto")
    .append("path")
    .attr("d", "M1 1L9 5L1 9")
    .attr("fill", "none")
    .attr("stroke", "#7F77DD")
    .attr("stroke-width", "1.5")
    .attr("stroke-linecap", "round")
    .attr("stroke-linejoin", "round");

  const root = svg.append("g").attr("class", "graph-root");
  const edgeLayer = root.append("g").attr("class", "edges");
  const labelLayer = root.append("g").attr("class", "edge-labels");
  const nodeLayer = root.append("g").attr("class", "nodes");

  function edgeCoords(link) {
    const sx = link.source.fx ?? link.source.x;
    const sy = link.source.fy ?? link.source.y;
    const tx = link.target.fx ?? link.target.x;
    const ty = link.target.fy ?? link.target.y;

    const dx = tx - sx;
    const dy = ty - sy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux = dx / dist;
    const uy = dy / dist;

    // Start: owner circle edge + small gap
    const startGap = link.source.r + 6;
    // End: target circle edge + gap for arrowhead
    const endGap = link.target.r + 12;

    return {
      x1: sx + ux * startGap,
      y1: sy + uy * startGap,
      x2: tx - ux * endGap,
      y2: ty - uy * endGap,
      mx: (sx + tx) / 2,
      my: (sy + ty) / 2,
    };
  }

  const edgeSel = edgeLayer
    .selectAll("line")
    .data(links)
    .enter()
    .append("line")
    .attr("stroke", (d) => (d.role === "selfDuplicate" ? "#7F77DD" : "#D85A30"))
    .attr("stroke-width", (d) => (d.role === "selfDuplicate" ? 1.5 : 1.2))
    .attr("stroke-dasharray", (d) =>
      d.role === "selfDuplicate" ? "5 3" : "none",
    )
    .attr("opacity", 0.45)
    .attr("fill", "none")
    .attr("marker-end", (d) =>
      d.role === "selfDuplicate" ? "url(#arr-self)" : "url(#arr-dup)",
    );

  function renderEdges() {
    links.forEach((link) => {
      const c = edgeCoords(link);
      link._coords = c;
    });

    edgeSel
      .attr("x1", (d) => d._coords.x1)
      .attr("y1", (d) => d._coords.y1)
      .attr("x2", (d) => d._coords.x2)
      .attr("y2", (d) => d._coords.y2);

  }

  const nodeGroups = nodeLayer
    .selectAll("g")
    .data(allNodes)
    .enter()
    .append("g")
    .attr("class", "node-g")
    .attr("transform", (d) => `translate(${d.fx},${d.fy})`);

  // Outer dashed ring for owner + self-duplicate
  nodeGroups
    .filter((d) => d.role === "owner" || d.role === "selfDuplicate")
    .append("circle")
    .attr("r", (d) => (d.role === "owner" ? d.r + 10 : d.r + 6))
    .attr("fill", "none")
    .attr("stroke", (d) => COLORS[d.role].ring)
    .attr("stroke-width", (d) => (d.role === "owner" ? 1.8 : 1.2))
    .attr("stroke-dasharray", (d) => (d.role === "owner" ? "5 3" : "3 2"))
    .attr("opacity", 0.4)
    .attr("class", "node-ring");

  // Main circle
  nodeGroups
    .append("circle")
    .attr("r", (d) => d.r)
    .attr("fill", (d) => COLORS[d.role].fill)
    .attr("class", "node-main");

  // Initials
  nodeGroups
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("y", (d) => (d.role === "owner" ? -6 : 0))
    .attr("fill", "#ffffff")
    .attr("font-size", (d) => (d.role === "owner" ? 15 : 12))
    .attr("font-weight", "500")
    .attr("pointer-events", "none")
    .text((d) => getInitials(d.username));

  // File-count sub-label inside owner node
  nodeGroups
    .filter((d) => d.role === "owner")
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("y", 11)
    .attr("fill", "rgba(255,255,255,0.65)")
    .attr("font-size", 9)
    .attr("pointer-events", "none")
    .text((d) => `${d.fileCount} copies`);

  // Name label below node
  nodeGroups
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "hanging")
    .attr("y", (d) => d.r + 10)
    .attr("fill", "var(--color-text-primary)")
    .attr("font-size", 11)
    .attr("font-weight", (d) => (d.role === "owner" ? "500" : "400"))
    .attr("pointer-events", "none")
    .text((d) => d.username.split(" ")[0]);

  // File-count badge (peripheral nodes)
  const badgeG = nodeGroups.filter((d) => d.role !== "owner");

  badgeG
    .append("circle")
    .attr("cx", (d) => d.r * 0.72)
    .attr("cy", (d) => -d.r * 0.72)
    .attr("r", 9)
    .attr("fill", (d) => COLORS[d.role].badge)
    .attr("stroke", (d) => COLORS[d.role].fill)
    .attr("stroke-width", 0.5);

  badgeG
    .append("text")
    .attr("x", (d) => d.r * 0.72)
    .attr("y", (d) => -d.r * 0.72)
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("fill", (d) => COLORS[d.role].badgeText)
    .attr("font-size", 8)
    .attr("font-weight", "500")
    .attr("pointer-events", "none")
    .text((d) => d.fileCount);

  nodeGroups
    .on("mouseenter", function (_, d) {
      d3.select(this)
        .select(".node-main")
        .transition()
        .duration(140)
        .attr("r", d.r * 1.07)
        .attr("fill", d3.color(COLORS[d.role].fill).brighter(0.25));
      d3.select(this)
        .select(".node-ring")
        .transition()
        .duration(140)
        .attr("opacity", 0.7);
    })
    .on("mouseleave", function (_, d) {
      d3.select(this)
        .select(".node-main")
        .transition()
        .duration(140)
        .attr("r", d.r)
        .attr("fill", COLORS[d.role].fill);
      d3.select(this)
        .select(".node-ring")
        .transition()
        .duration(140)
        .attr("opacity", 0.4);
    });

  let dragMoved = false;

  const drag = d3
    .drag()
    .on("start", function (event, d) {
      dragMoved = false;
      // Prevent pan from activating while dragging a node
      event.sourceEvent.stopPropagation();
      d3.select(this).raise(); // bring to front
    })
    .on("drag", function (event, d) {
      dragMoved = true;

      if (d.role === "owner") {
        // Move owner + all peripheral nodes together (maintain relative offsets)
        const dx = event.dx;
        const dy = event.dy;

        allNodes.forEach((n) => {
          n.fx = (n.fx ?? n.x) + dx;
          n.fy = (n.fy ?? n.y) + dy;
        });

        // Batch-update all node positions
        nodeGroups.attr("transform", (n) => `translate(${n.fx},${n.fy})`);
      } else {
        // Move only this peripheral node
        d.fx = event.x;
        d.fy = event.y;
        d3.select(this).attr("transform", `translate(${d.fx},${d.fy})`);
      }

      renderEdges();
    })
    .on("end", function (event, d) {
      // If no real movement → treat as click
      if (!dragMoved && d.role !== "owner") {
        onUserClick(toUserRow(d.entry, ownerActorId));
      }
    });

  nodeGroups.call(drag);
  renderEdges();
  container.__stopSim = () => {}; // nothing to stop — no simulation
}



export default function MapTab({ duplicateUserData, drive, onUserClick }) {
  const containerRef = useRef(null);
  const loader = useSelector((state) => state.dataTagReducer.popupLoader);

  const handleUserClick = useCallback(
    (row) => {
      if (typeof onUserClick === "function") onUserClick(row);
    },
    [onUserClick],
  );

  useEffect(() => {
    if (!containerRef.current) return;
    buildGraph(
      containerRef.current,
      duplicateUserData?.content ?? [],
      drive,
      handleUserClick,
    );
    return () => containerRef.current?.__stopSim?.();
  }, [duplicateUserData, drive, handleUserClick]);

  const hasData = (duplicateUserData?.content?.length ?? 0) > 0;

  return (
    <div className="map-tab">
    {loader && (
                  <img
                    src={Page_Loader}
                    alt="loading"
                    className="loader-data-source dataTagPopupLoader"
                  ></img>
                )}
      {!hasData ? (
        <div className="map-tab__empty">No duplicate data available</div>
      ) : (
        <>
          <div className="map-tab__canvas" ref={containerRef} />
          <div className="map-tab__toolbar">
            <div className="map-tab__legend">
              <span className="map-tab__legend-item">
                <span className="map-tab__dot map-tab__dot--owner" />
                Drive owner
              </span>
              <span className="map-tab__legend-item">
                <span className="map-tab__dot map-tab__dot--self" />
                Self-duplicate
              </span>
              <span className="map-tab__legend-item">
                <span className="map-tab__dot map-tab__dot--dup" />
                Duplicate users
              </span>
              <span className="map-tab__legend-hint">
                Drag owner to move all · Drag node to reposition · Scroll to
                zoom
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
